import { useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronDown } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

/**
 * Depoimentos reais coletados do Google Meu Negócio.
 * Carrossel compacto: 1 card no mobile, 2 no tablet, 3 no desktop.
 * Texto longo colapsa em "ler mais" para manter alturas equilibradas.
 */

const testimonials = [
  {
    name: "Priscila Morinigo",
    meta: "Avaliação Neuropsicológica · há 1 mês",
    text: "Eu tive uma experiência ótima com a Equilybra. Comunicação fácil e direta, cordialidade, respeito... Mas quero destacar o atendimento da Dra. Camila, numa avaliação neuropsicológica. Eu nunca me senti tão à vontade com alguém que eu não conheço como estive com ela... Uma profissional fenomenal, acolhedora, divertidíssima e super querida. Eu recomendo de olhos fechados!",
    rating: 5,
  },
  {
    name: "Luciana Campos Nishimura",
    meta: "Avaliação Neuropsicológica · há 3 semanas",
    text: "Gostaria de expressar minha profunda gratidão à Dra. Camila Louredo Sales pelo excelente trabalho realizado durante minha avaliação neuropsicológica. Ao longo do processo, ela demonstrou não apenas grande conhecimento técnico, mas também sensibilidade, empatia e profissionalismo. Pela primeira vez, consegui compreender com clareza a origem de muitas dificuldades que enfrentei ao longo da vida. Mais do que um diagnóstico, ela me proporcionou autoconhecimento, acolhimento e uma nova perspectiva sobre mim mesma.",
    rating: 5,
  },
  {
    name: "Mário",
    meta: "Local Guide · há 2 meses",
    text: "Uma ótima profissional, muito simpática, adora crianças. Me senti muito à vontade nas terapias que fiz. Cada vez que vou ao consultório, volto muito mais confiante. Ela é um anjo na vida das pessoas que a procuram. Super recomendo.",
    rating: 5,
  },
  {
    name: "Franciele Gomes",
    meta: "Avaliação Neuropsicológica Infantil · há 6 meses",
    text: "Minha experiência com a Clínica Equilybra foi excelente. Desde a recepção até o atendimento com o Dr. Vinícius, fui muito bem acolhida. Meu filho precisou fazer uma avaliação neuropsicológica e, além do atendimento humanizado e profissional, encontrei um valor muito mais acessível do que em outros lugares. Recomendo muito a clínica e agradeço a toda a equipe pelo cuidado e atenção.",
    rating: 5,
  },
  {
    name: "Fragozo Fernandes",
    meta: "Paciente · há 2 semanas",
    text: "Clínica incrível! Ambiente tranquilo, profissionais competentes e um atendimento que realmente faz diferença. Recomendo de olhos fechados.",
    rating: 5,
  },
  {
    name: "Patricia Souza Alves",
    meta: "Local Guide · há 4 meses",
    text: "Minha experiência com a neuropsicóloga Camila foi excelente. Ela é um amor de pessoa, extremamente atenciosa e acolhedora. Desde o início me deixou muito à vontade, demonstrando compreensão em tudo o que foi conversado. Explicou cada teste de forma clara e detalhada e, no momento do resultado, apresentou cada etapa realizada com observações cuidadosas. Recomendo demais!",
    rating: 5,
  },
  {
    name: "Victor Seichi Shimamoto",
    meta: "Local Guide · há 4 meses",
    text: "Ótimo atendimento. Desde o primeiro contato, muita cortesia e gentileza. A neuropsicóloga Camila, em todo momento, foi extremamente gentil, solícita, profissional e atenta. Só tenho a agradecer à clínica e aos profissionais.",
    rating: 5,
  },
  {
    name: "Elisângela Campos",
    meta: "Local Guide · há 5 meses",
    text: "Durante todo o período de tratamento do meu filho fomos bem atendidos e compreendidos, tanto pelas psicólogas que o trataram quanto pela administração, que respondia com muita atenção nossas dúvidas e nos auxiliava quando possível. Indico a clínica para quem preza pelo respeito e bom trabalho.",
    rating: 5,
  },
];

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const CHAR_LIMIT = 220;

const TestimonialCard = ({ t }: { t: (typeof testimonials)[number] }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = t.text.length > CHAR_LIMIT;
  const displayText = expanded || !isLong ? t.text : t.text.slice(0, CHAR_LIMIT).trimEnd() + "…";

  return (
    <article className="bg-card rounded-2xl p-6 border border-border shadow-soft flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <Quote className="w-7 h-7 text-primary/40" aria-hidden="true" />
        <div className="flex items-center gap-1" role="img" aria-label={`Avaliação ${t.rating} de 5`}>
          {Array.from({ length: t.rating }).map((_, k) => (
            <Star key={k} className="w-4 h-4 fill-primary text-primary" />
          ))}
        </div>
      </div>

      <p className="text-foreground/90 leading-relaxed italic text-[15px]">
        "{displayText}"
      </p>

      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline font-medium self-start"
        >
          {expanded ? "Ler menos" : "Ler mais"}
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}

      <div className="flex items-center gap-3 pt-4 mt-auto border-t border-border">
        <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center font-heading font-bold text-primary shrink-0">
          {getInitials(t.name)}
        </div>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-foreground truncate">{t.name}</div>
          <div className="text-xs text-muted-foreground truncate">{t.meta}</div>
        </div>
      </div>
    </article>
  );
};

const Testimonials = () => {
  return (
    <section aria-labelledby="depoimentos-heading" className="py-16 md:py-24 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <div className="flex items-center gap-0.5" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, k) => (
                <Star key={k} className="w-4 h-4 fill-primary text-primary" />
              ))}
            </div>
            <span className="text-sm font-semibold text-foreground">5,0 no Google</span>
          </div>
          <h2
            id="depoimentos-heading"
            className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3"
          >
            Quem já veio conta como foi
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Depoimentos reais publicados no Google por pacientes da clínica — não são frases de propaganda.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          <Carousel
            opts={{ align: "start", loop: true }}
            plugins={[Autoplay({ delay: 6000, stopOnInteraction: true })]}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((t, i) => (
                <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <TestimonialCard t={t} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4" />
            <CarouselNext className="hidden md:flex -right-4" />
          </Carousel>
        </motion.div>

        <div className="text-center mt-10">
          <a
            href="https://www.google.com/search?q=Equilybra+Psicologia+Ipiranga"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
          >
            Ver todas as avaliações no Google →
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
